import { useState, useEffect, useRef } from "react";
import "../../style/scss/AutoComplete.scss";

function optionLabel(option) {
  if (option == null) return "";
  if (typeof option === "string") return option;
  return option.label || option.Name || option.name || String(option.value ?? "");
}

function optionSecondary(option) {
  if (!option || typeof option === "string") return "";
  return option.secondary || option.ShortName || option.shortName || "";
}

function optionKey(option, index) {
  if (option && typeof option === "object") {
    return option.id ?? option.ID ?? option.value ?? `${optionLabel(option)}-${index}`;
  }
  return `${option}-${index}`;
}

/**
 * @param {object} props
 * @param {Array<string|{label:string, secondary?:string, value?:any}>} [props.options]
 * @param {boolean} [props.filterLocally=true] — set false when options are already filtered by the server
 */
export default function Autocomplete({
  options = [],
  onSelect,
  onChange,
  onInputChange,
  value,
  placeholder,
  className,
  label,
  filterLocally = true,
  emptyMessage = "",
}) {
  const [inputValue, setInputValue] = useState(value || "");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const rootRef = useRef(null);
  const listId = useRef(`ac-list-${Math.random().toString(36).slice(2, 9)}`).current;

  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const list = Array.isArray(options) ? options : [];
    const needle = (inputValue || "").toLowerCase().trim();

    let next;
    if (!filterLocally) {
      next = list.filter((option) => optionLabel(option).trim().length > 0);
    } else {
      next = list.filter((option) => {
        const labelText = optionLabel(option);
        if (!labelText) return false;
        if (!needle) return true;
        const secondary = optionSecondary(option).toLowerCase();
        return (
          labelText.toLowerCase().includes(needle) ||
          (secondary && secondary.includes(needle))
        );
      });
    }

    setFilteredOptions(next);
    setHighlightIndex((idx) => (idx >= next.length ? next.length - 1 : idx));
  }, [inputValue, options, filterLocally]);

  // Keep the dropdown open when async server results arrive while the user is typing.
  useEffect(() => {
    if ((options?.length || 0) > 0 && (inputValue || "").trim().length >= 2) {
      setShowSuggestions(true);
    }
    if (showSuggestions && filteredOptions.length > 0) setHighlightIndex(0);
  }, [options, filteredOptions, showSuggestions, inputValue]);

  const handleInputChange = (e) => {
    const next = e.target.value;
    setInputValue(next);
    setShowSuggestions(true);
    setHighlightIndex(0);
    if (onInputChange) onInputChange(e, next);
  };

  const choose = (option) => {
    const isCreate = !!(option && typeof option === "object" && option.isCreate);
    const labelText = optionLabel(option);
    if (!isCreate) {
      setInputValue(labelText);
    }
    setShowSuggestions(false);
    setHighlightIndex(-1);
    if (onSelect) onSelect(labelText, option);
    if (onChange) onChange(null, labelText, option);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setInputValue("");
      setShowSuggestions(false);
      setHighlightIndex(-1);
      if (onInputChange) onInputChange(e, "");
      return;
    }

    if (!showSuggestions || filteredOptions.length === 0) {
      if (e.key === "ArrowDown" && filteredOptions.length > 0) {
        setShowSuggestions(true);
        setHighlightIndex(0);
        e.preventDefault();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % filteredOptions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => (i <= 0 ? filteredOptions.length - 1 : i - 1));
    } else if (e.key === "Enter" && highlightIndex >= 0) {
      e.preventDefault();
      choose(filteredOptions[highlightIndex]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setShowSuggestions(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const open =
    showSuggestions &&
    (filteredOptions.length > 0 || (emptyMessage && (inputValue || "").trim().length >= 2));

  return (
    <div className={"autocomplete " + (className || "")} ref={rootRef}>
      {label ? <label className="form-label">{label}</label> : null}
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          if ((inputValue || "").trim().length >= 1 || filteredOptions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || "Type to search..."}
        aria-label={label || placeholder || "Autocomplete"}
        aria-autocomplete="list"
        aria-expanded={open ? "true" : "false"}
        aria-controls={listId}
        role="combobox"
        autoComplete="off"
      />
      {open ? (
        <ul className="suggestions" id={listId} role="listbox" data-testid="autocomplete-suggestions">
          {filteredOptions.length === 0 ? (
            <li className="suggestions__empty" role="option" aria-disabled="true">
              {emptyMessage}
            </li>
          ) : (
            filteredOptions.map((option, index) => {
              const labelText = optionLabel(option);
              const secondary = optionSecondary(option);
              const showSecondary =
                secondary && secondary.toLowerCase() !== labelText.toLowerCase();
              const isCreate = !!(option && typeof option === "object" && option.isCreate);
              return (
                <li
                  key={optionKey(option, index)}
                  role="option"
                  aria-selected={index === highlightIndex}
                  className={[
                    index === highlightIndex ? "is-highlighted" : "",
                    isCreate ? "suggestions__create" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onMouseDown={(e) => {
                    // mousedown so selection happens before input blur
                    e.preventDefault();
                    choose(option);
                  }}
                  onMouseEnter={() => setHighlightIndex(index)}
                >
                  <span className="suggestions__label">{labelText}</span>
                  {showSecondary ? (
                    <span className="suggestions__secondary">{secondary}</span>
                  ) : null}
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
